import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { Loader2, PenLine, RotateCcw, Save, X, Link2, ImagePlus, Sparkles, Languages } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";
import { api, formatApiError } from "../lib/api";
import { translations } from "../i18n/translations";
import { RichField, getAt } from "./admin/ContentEditor";

const POP_W = 460;

// Mode « page Word » pour l'admin : le crayon active le mode modification, puis un
// clic sur un TEXTE, une IMAGE ou un BOUTON/LIEN de la vraie page ouvre un
// mini-éditeur à cet endroit. Textes : par langue (basculez FR/EN pour modifier
// l'autre version). Images et liens : réglages « site|... » (communs aux 2 langues),
// avec téléversement de fichier possible pour les images. « Réinitialiser » ramène
// la version d'origine. Réservé aux administrateurs.
export default function InlineEditMode() {
  const { user } = useAuth();
  const { lang, editMode, setEditMode, overrides, setOverride } = useLang();
  const [editing, setEditing] = useState(null);   // { kind: text|image|link, path, current, left, top, linkPath?, linkCurrent? }
  const [draft, setDraft] = useState("");         // texte (kind text) ou URL d'image (kind image)
  const [linkDraft, setLinkDraft] = useState(""); // cible du lien (kind link, ou bouton autour d'un texte)
  const [aiBusy, setAiBusy] = useState(false);    // proposition Gemini en cours (champs texte)
  const [aiNote, setAiNote] = useState("");       // consigne facultative pour Gemini
  const [aiImgBusy, setAiImgBusy] = useState(false);  // génération d'image Gemini en cours
  const [aiImgPrompt, setAiImgPrompt] = useState(""); // description du visuel à générer
  const [trBusy, setTrBusy] = useState(false);        // traduction IA de l'autre langue en cours
  const [busy, setBusy] = useState(false);

  // Admin — ou vendeur dont la case « Modifier les pages » est cochée (demande du
  // propriétaire : option offerte côté vendeur).
  const canEdit = user?.role === "admin"
    || (user?.role === "vendeur" && (user?.vendeur_actions || []).includes("modifier_pages"));

  // Écrans de TRAVAIL (/admin, /vendeur — demande du propriétaire, 2026-10-22) :
  // la pastille « Modifier la page » n'y a aucun contenu éditable et recouvrait
  // les fiches sur téléphone — masquée au rendu (pas de retour anticipé : les
  // hooks plus bas doivent toujours s'exécuter dans le même ordre).
  const chemin = useLocation().pathname.replace(/^\/en(?=\/)/, "");
  const ecranTravail = chemin === "/admin" || chemin === "/vendeur";

  // Clic sur un élément de la page (mode actif) : ouvre l'éditeur à cet endroit.
  // Les clics hors élément éditable (navigation, menus, bascule FR/EN) restent normaux.
  useEffect(() => {
    if (!editMode || !canEdit) return undefined;
    const onClick = (e) => {
      if (e.target.closest?.("[data-inline-editor]")) return;  // clics dans l'éditeur : libres
      const imgEl = e.target.closest?.("[data-edit-image]");
      const txtEl = e.target.closest?.("[data-edit-path]");
      const linkEl = e.target.closest?.("[data-edit-link]");
      const el = imgEl || txtEl || linkEl;
      if (!el) return;
      e.preventDefault();
      e.stopPropagation();
      const r = el.getBoundingClientRect();
      const left = Math.round(Math.min(Math.max(8, r.left), Math.max(8, window.innerWidth - POP_W - 8)));
      const below = r.bottom + 8;
      const top = Math.round(below + 400 > window.innerHeight ? Math.max(8, r.top - 408) : below);
      if (imgEl) {
        const path = imgEl.getAttribute("data-edit-image");
        const current = imgEl.getAttribute("src") || "";
        setEditing({ kind: "image", path, current, left, top });
        setDraft(overrides[`site|${path}`] ?? current);
      } else if (txtEl) {
        // Texte — et si ce texte est le libellé d'un bouton/lien modifiable,
        // le mini-éditeur offre AUSSI la cible du lien à modifier.
        const path = txtEl.getAttribute("data-edit-path");
        const anc = txtEl.closest("[data-edit-link]");
        const linkPath = anc ? anc.getAttribute("data-edit-link") : null;
        const linkCurrent = anc ? (anc.getAttribute("href") || "") : "";
        setEditing({ kind: "text", path, linkPath, linkCurrent, left, top });
        // Texte d'origine : traduction connue, sinon le texte affiché tel quel
        // (chaîne codée en dur rendue modifiable via tx()).
        setDraft(overrides[`${lang}|${path}`] ?? getAt(translations[lang], path) ?? (txtEl.textContent || ""));
        setLinkDraft(anc ? (overrides[`site|${linkPath}`] ?? linkCurrent) : "");
      } else {
        const path = linkEl.getAttribute("data-edit-link");
        const current = linkEl.getAttribute("href") || "";
        setEditing({ kind: "link", path, current, left, top });
        setLinkDraft(overrides[`site|${path}`] ?? current);
      }
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [editMode, canEdit, lang, overrides]);

  if (!canEdit || ecranTravail) return null;

  const close = () => setEditing(null);

  // Enregistre un réglage « site » (image ou lien). Vide ou inchangé = retour à
  // l'original (pas de personnalisation inutile figée). Retourne true si modifié.
  const saveSite = async (path, value, current) => {
    const v = (value || "").trim();
    if (!v || v === current) {
      await api.delete("/admin/content", { params: { lang: "site", path } });
      setOverride("site", path, null);
      return false;
    }
    await api.put("/admin/content", { lang: "site", path, value: v });
    setOverride("site", path, v);
    return true;
  };

  const save = async () => {
    setBusy(true);
    try {
      let changed = false;
      if (editing.kind === "text") {
        const def = getAt(translations[lang], editing.path) ?? "";
        if (!draft.trim() || draft === def) {
          await api.delete("/admin/content", { params: { lang, path: editing.path } });
          setOverride(lang, editing.path, null);
        } else {
          await api.put("/admin/content", { lang, path: editing.path, value: draft });
          setOverride(lang, editing.path, draft);
          changed = true;
        }
        if (editing.linkPath) changed = (await saveSite(editing.linkPath, linkDraft, editing.linkCurrent)) || changed;
      } else if (editing.kind === "image") {
        changed = await saveSite(editing.path, draft, editing.current);
      } else {
        changed = await saveSite(editing.path, linkDraft, editing.current);
      }
      toast.success(changed ? "Enregistré — visible sur le site." : "Revenu à la version d'origine.");
      close();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    try {
      if (editing.kind === "text") {
        await api.delete("/admin/content", { params: { lang, path: editing.path } });
        setOverride(lang, editing.path, null);
        if (editing.linkPath) {
          await api.delete("/admin/content", { params: { lang: "site", path: editing.linkPath } });
          setOverride("site", editing.linkPath, null);
        }
      } else {
        await api.delete("/admin/content", { params: { lang: "site", path: editing.path } });
        setOverride("site", editing.path, null);
      }
      toast.success("Revenu à la version d'origine.");
      close();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  // Téléversement d'une image (stockage objet du site) — l'URL remplit le champ,
  // « Enregistrer » applique.
  const uploadImage = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/uploads/image", fd);
      setDraft(data.url);
      toast.success("Image téléversée — cliquez Enregistrer pour l'appliquer.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  // Proposition Gemini (clé personnelle du propriétaire) : remplit le champ texte
  // d'une suggestion retravaillée — à retoucher puis « Enregistrer ».
  const aiSuggest = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/admin/content/ai-suggest", {
        current: draft, lang, path: editing.path, instruction: aiNote,
      });
      setDraft(data.suggestion || "");
      toast.success("Proposition Gemini insérée — retouchez-la puis Enregistrer.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAiBusy(false);
    }
  };

  // Génération d'un visuel par Gemini (demande du propriétaire) : l'URL générée
  // remplit le champ image — « Enregistrer » applique.
  const aiGenerate = async () => {
    if (aiImgPrompt.trim().length < 4) { toast.error("Décrivez l'image à générer"); return; }
    setAiImgBusy(true);
    try {
      const { data } = await api.post("/admin/content/ai-image", { prompt: aiImgPrompt.trim() });
      setDraft(data.url);
      toast.success("Visuel Gemini généré — cliquez Enregistrer pour l'appliquer.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAiImgBusy(false);
    }
  };

  // Traduction IA en UN CLIC (demande du propriétaire) : traduit le texte du champ
  // et enregistre directement la personnalisation de l'AUTRE langue (réinitialisable).
  const translateOther = async () => {
    if (!draft.trim()) { toast.error("Le champ est vide — rien à traduire"); return; }
    const target = lang === "fr" ? "en" : "fr";
    setTrBusy(true);
    try {
      const { data } = await api.post("/admin/content/ai-translate", { text: draft, to: target });
      await api.put("/admin/content", { lang: target, path: editing.path, value: data.translation });
      setOverride(target, editing.path, data.translation);
      toast.success(`Version ${target === "en" ? "anglaise" : "française"} remplie : « ${(data.translation || "").slice(0, 90)} »`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setTrBusy(false);
    }
  };

  const siteOverride = editing && (
    editing.kind === "text"
      ? (editing.linkPath && overrides[`site|${editing.linkPath}`] !== undefined)
      : overrides[`site|${editing.path}`] !== undefined
  );
  const textOverride = editing && editing.kind === "text" && overrides[`${lang}|${editing.path}`] !== undefined;

  return (
    <>
      {!editMode ? (
        <button type="button" onClick={() => setEditMode(true)} data-testid="inline-edit-toggle"
          title="Modifier les textes, images et boutons directement sur la page"
          className="fixed bottom-5 left-5 z-[9990] inline-flex items-center gap-2 bg-gold text-ink font-bold text-xs px-4 py-2.5 rounded-full shadow-[0_8px_24px_rgba(0,0,0,0.5)] hover:bg-champagne transition-colors duration-300">
          <PenLine className="w-4 h-4" /> Modifier la page
        </button>
      ) : (
        <div data-inline-editor="1" data-testid="inline-edit-bar"
          className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[9990] flex items-center gap-3 max-w-[calc(100vw-16px)] bg-surface border border-gold/50 rounded-full px-4 sm:px-5 py-2.5 shadow-[0_8px_30px_rgba(0,0,0,0.6)]">
          <PenLine className="w-4 h-4 text-gold shrink-0" />
          <p className="text-xs text-ivory/80 whitespace-nowrap">
            <span className="sm:hidden">Mode modification</span>
            <span className="hidden sm:inline">Mode modification — cliquez un texte, une image ou un bouton pour le modifier
            <span className="text-gold"> (version {lang === "fr" ? "française" : "anglaise"})</span></span>
          </p>
          <button type="button" onClick={() => { setEditMode(false); setEditing(null); }} data-testid="inline-edit-done"
            className="inline-flex items-center gap-1.5 bg-gold text-ink text-xs font-bold px-4 py-1.5 rounded-full hover:bg-champagne transition-colors">
            <X className="w-3.5 h-3.5" /> Terminer
          </button>
        </div>
      )}
      {editMode && editing && createPortal(
        <div data-inline-editor="1" data-testid="inline-edit-pop"
          className="fixed z-[9999] bg-surface border border-gold/50 rounded-xl shadow-[0_16px_50px_rgba(0,0,0,0.7)] p-4"
          style={{ left: editing.left, top: editing.top, width: `min(${POP_W}px, calc(100vw - 16px))` }}>
          <div className="flex items-center justify-between gap-3 mb-2">
            <p className="text-[10px] font-mono text-ivory/45 truncate" data-testid="inline-edit-path">{editing.path}</p>
            <button type="button" onClick={close} data-testid="inline-edit-close" title="Fermer"
              className="text-ivory/50 hover:text-gold transition-colors"><X className="w-4 h-4" /></button>
          </div>

          {editing.kind === "text" && (
            <>
              <RichField testId="inline-edit-field" value={draft} onChange={setDraft} />
              {/* Proposition IA (demande du propriétaire) : Gemini retravaille le texte,
                  consigne facultative — le résultat reste modifiable avant d'enregistrer */}
              <div className="mt-3 border border-gold/25 rounded-lg p-2.5 bg-gold/5 space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <button type="button" onClick={aiSuggest} disabled={aiBusy} data-testid="inline-edit-ai"
                    className="inline-flex items-center gap-1.5 bg-gold/15 border border-gold/40 text-gold text-xs font-semibold px-3.5 py-1.5 rounded-full hover:bg-gold hover:text-ink transition-colors disabled:opacity-60">
                    {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    Proposition Gemini
                  </button>
                  {/* Traduction IA en un clic : remplit directement la version de
                      l'AUTRE langue (demande du propriétaire) */}
                  <button type="button" onClick={translateOther} disabled={trBusy} data-testid="inline-edit-translate"
                    title={lang === "fr" ? "Traduire ce texte et remplir la version anglaise" : "Traduire ce texte et remplir la version française"}
                    className="inline-flex items-center gap-1.5 bg-gold/15 border border-gold/40 text-gold text-xs font-semibold px-3.5 py-1.5 rounded-full hover:bg-gold hover:text-ink transition-colors disabled:opacity-60">
                    {trBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Languages className="w-3.5 h-3.5" />}
                    {trBusy ? "Traduction…" : (lang === "fr" ? "Traduire en anglais" : "Traduire en français")}
                  </button>
                  <span className="text-[10px] text-ivory/45">à retoucher avant d'enregistrer</span>
                </div>
                <input value={aiNote} onChange={(e) => setAiNote(e.target.value)}
                  data-testid="inline-edit-ai-note" placeholder="Consigne facultative : « plus chaleureux », « plus court »…"
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-1.5 text-xs focus:border-gold focus:outline-none" />
              </div>
              {editing.linkPath && (
                <label className="block mt-3">
                  <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-ivory/65 mb-1">
                    <Link2 className="w-3 h-3" /> Cible du bouton (lien)
                  </span>
                  <input value={linkDraft} onChange={(e) => setLinkDraft(e.target.value)}
                    data-testid="inline-edit-link-url" placeholder={editing.linkCurrent || "/boutique ou https://…"}
                    className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm focus:border-gold focus:outline-none" />
                </label>
              )}
            </>
          )}

          {editing.kind === "image" && (
            <div data-testid="inline-edit-image">
              <div className="mb-3 h-36 rounded-lg border border-ivory/10 bg-ink flex items-center justify-center overflow-hidden">
                {draft
                  ? <img src={draft} alt="Aperçu" data-testid="inline-edit-image-preview" className="max-h-full max-w-full object-contain" />
                  : <ImagePlus className="w-6 h-6 text-ivory/30" />}
              </div>
              <input value={draft} onChange={(e) => setDraft(e.target.value)}
                data-testid="inline-edit-image-url" placeholder="URL de l'image — https://…"
                className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm focus:border-gold focus:outline-none mb-2" />
              <label
                className="inline-flex items-center gap-2 border border-gold/40 text-gold text-xs font-semibold px-4 py-2 rounded-full hover:bg-gold hover:text-ink transition-colors cursor-pointer">
                <ImagePlus className="w-3.5 h-3.5" /> Téléverser un fichier…
                <input type="file" accept="image/*" className="hidden" data-testid="inline-edit-image-file"
                  onChange={(e) => { uploadImage(e.target.files?.[0]); e.target.value = ""; }} />
              </label>
              {/* Génération par Gemini (demande du propriétaire) : décrire le visuel
                  plutôt que téléverser un fichier */}
              <div className="mt-3 border border-gold/25 rounded-lg p-2.5 bg-gold/5 space-y-2">
                <div className="flex items-center gap-2">
                  <button type="button" onClick={aiGenerate} disabled={aiImgBusy} data-testid="inline-edit-ai-image"
                    className="inline-flex items-center gap-1.5 bg-gold/15 border border-gold/40 text-gold text-xs font-semibold px-3.5 py-1.5 rounded-full hover:bg-gold hover:text-ink transition-colors disabled:opacity-60">
                    {aiImgBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    {aiImgBusy ? "Gemini dessine… (15-30 s)" : "Générer avec Gemini"}
                  </button>
                  <span className="text-[10px] text-ivory/45">puis Enregistrer</span>
                </div>
                <input value={aiImgPrompt} onChange={(e) => setAiImgPrompt(e.target.value)}
                  data-testid="inline-edit-ai-image-prompt" placeholder="Décrivez le visuel : « médaille d'or sur ruban rouge, fond sombre »…"
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-1.5 text-xs focus:border-gold focus:outline-none" />
              </div>
              <p className="text-[10px] text-ivory/45 mt-2">Champ vide = image d'origine.</p>
            </div>
          )}

          {editing.kind === "link" && (
            <label className="block" data-testid="inline-edit-link">
              <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-ivory/65 mb-1">
                <Link2 className="w-3 h-3" /> Cible du lien (URL)
              </span>
              <input value={linkDraft} onChange={(e) => setLinkDraft(e.target.value)}
                data-testid="inline-edit-link-url-only" placeholder="/boutique ou https://…"
                className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm focus:border-gold focus:outline-none" />
              <span className="block text-[10px] text-ivory/45 mt-1.5">Champ vide = cible d'origine ({editing.current}).</span>
            </label>
          )}

          <div className="flex gap-2.5 mt-3">
            <button type="button" onClick={save} disabled={busy} data-testid="inline-edit-save"
              className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-4 py-2 rounded-full text-xs hover:bg-champagne transition-colors disabled:opacity-60">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Enregistrer
            </button>
            {(textOverride || siteOverride) && (
              <button type="button" onClick={reset} disabled={busy} data-testid="inline-edit-reset"
                className="inline-flex items-center gap-2 border border-ivory/20 text-ivory/60 px-4 py-2 rounded-full text-xs hover:border-gold/50 hover:text-gold transition-colors disabled:opacity-60">
                <RotateCcw className="w-3.5 h-3.5" /> Réinitialiser
              </button>
            )}
          </div>
        </div>,
        document.body)}
    </>
  );
}
